import type {
  CardFilter,
  DownloadFileResponse,
  FileDetailResponse,
  ListCardsResponse,
  ListFilesResponse,
  UploadResponse,
} from '@eloquor/api-contract';
import { apiFetch, apiFetchMultipart, apiFetchMultipartWithProgress } from './client';

export async function uploadFile(file: File): Promise<UploadResponse> {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiFetchMultipart<UploadResponse>('/files', form);
}

/**
 * Upload variant that reports upload progress to the caller (0..1). Falls
 * back to a no-progress fetch when the runtime lacks XHR (jsdom inside
 * vitest does support XHR, so this is here as belt-and-braces).
 */
export async function uploadFileWithProgress(
  file: File,
  onProgress: (fraction: number) => void
): Promise<UploadResponse> {
  const form = new FormData();
  form.append('file', file, file.name);
  if (typeof XMLHttpRequest === 'undefined') {
    return apiFetchMultipart<UploadResponse>('/files', form);
  }
  return apiFetchMultipartWithProgress<UploadResponse>('/files', form, onProgress);
}

export async function listFiles(): Promise<ListFilesResponse> {
  return apiFetch<ListFilesResponse>('/files');
}

export async function getFile(id: string): Promise<FileDetailResponse> {
  return apiFetch<FileDetailResponse>(`/files/${encodeURIComponent(id)}`);
}

export async function deleteFile(id: string): Promise<void> {
  await apiFetch<{ ok: true }>(`/files/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function listCards(id: string, filter: CardFilter = {}): Promise<ListCardsResponse> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  const query = qs.toString();
  return apiFetch<ListCardsResponse>(
    `/files/${encodeURIComponent(id)}/cards${query ? `?${query}` : ''}`
  );
}

/**
 * Ask the server for a short-lived signed download URL for the file's
 * original .docx. The frontend then opens that URL (which the browser
 * treats as a download because of the Content-Disposition header on the
 * S3 object).
 */
export async function getDownloadUrl(id: string): Promise<DownloadFileResponse> {
  return apiFetch<DownloadFileResponse>(`/files/${encodeURIComponent(id)}/download`);
}
